import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../hooks/useAuth';
import { Reveal } from './_shared';
import { JourneyCards, ListingRow, PhoneTop, StatusChip, type JourneyCard } from './JourneyCards';
import jeddahImg from '../../../assets/cities/jeddah.webp';
import riyadhImg from '../../../assets/cities/riyadh.webp';

// Three primary journeys as three EQUAL cards (owner, 2026-10-07): add, request,
// invest. The card frame (wasalt.sa's promo-card pattern, subgrid-aligned) and
// the mockup pieces live in JourneyCards.tsx, shared with the closing row.
//
// "List your property" resolves to the same target the nav uses:
// brokers/companies → /properties/new, everyone else →
// /individual-property-request (auth-gated, so guests are prompted to sign in).

// Mockup UI copy. Illustrative screens, not data: no figure here is presented
// as a market fact (the invest mockup deliberately carries none).
const MOCK = {
  ar: {
    myListings: 'إعلاناتي',
    row1: 'شقة · جدة',
    row2: 'فيلا · الرياض',
    perYear: '/سنة',
    published: 'منشور',
    review: 'قيد المراجعة',
    ask: 'أبحث عن شقة 3 غرف في جدة',
    broker: 'وسيط مرخّص',
    reply1: 'لديّ عقاران يناسبان طلبك',
    reply2: 'أرسلت لك 3 خيارات قريبة',
    metric: 'سعر المتر · حي النرجس',
    low: 'الأدنى',
    mid: 'الوسيط',
    high: 'الأعلى',
    source: 'من صفقات مسجّلة لدى وزارة العدل',
  },
  en: {
    myListings: 'My listings',
    row1: 'Apartment · Jeddah',
    row2: 'Villa · Riyadh',
    perYear: '/yr',
    published: 'Published',
    review: 'In review',
    ask: 'Looking for a 3-bedroom apartment in Jeddah',
    broker: 'Licensed broker',
    reply1: 'I have 2 homes that match',
    reply2: 'Sent you 3 options nearby',
    metric: 'Price per m² · Al Narjis',
    low: 'Low',
    mid: 'Median',
    high: 'High',
    source: 'From deals registered with the MoJ',
  },
} as const;
type Mock = (typeof MOCK)[keyof typeof MOCK];

export default function ActionCards() {
  const { t, i18n } = useTranslation(['home']);
  const isRTL = i18n.dir() === 'rtl';
  const m: Mock = isRTL ? MOCK.ar : MOCK.en;
  const { user } = useAuth();

  const listHref = useMemo(() => {
    const acct = (user?.accountType as string | undefined) ?? '';
    return acct === 'broker' || acct === 'company' ? '/properties/new' : '/individual-property-request';
  }, [user]);

  const card = (key: string, to: string, visual: JourneyCard['visual']): JourneyCard => ({
    key,
    to,
    title: t(`home.v2.actions.${key}.title`),
    body: t(`home.v2.actions.${key}.body`),
    cta: t(`home.v2.actions.${key}.cta`),
    visual,
  });

  const cards = [
    card('list', listHref, <ListMock m={m} />),
    card('request', '/client-requests/new', <RequestMock m={m} />),
    // The deed-based market tools (registered MoJ deals + REGA rent). The
    // older /market-insights page shows «قريباً» unless an admin toggle is on.
    card('invest', '/market-tools', <InvestMock m={m} />),
  ];

  return (
    <section className="bg-white pb-14 sm:pb-20">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <JourneyCards cards={cards} />
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Mockups. Decorative (the card text carries the meaning), so aria-hidden at
// the stage. Rows rise in once when scrolled into view (Reveal), then stay.
// ---------------------------------------------------------------------------

/** أضف عقارك — your listings, one live and one in review. */
function ListMock({ m }: { m: Mock }) {
  return (
    <>
      <PhoneTop>
        <p className="text-[11px] font-semibold text-[var(--pv-color-text-muted)]">{m.myListings}</p>
      </PhoneTop>
      {/* Rows sit wider than the phone and overlap its edges. */}
      <div className="absolute bottom-6 left-1/2 w-[244px] -translate-x-1/2 space-y-2">
        <ListingRow img={jeddahImg} title={m.row1} price="725,000" chip={<StatusChip tone="live">{m.published}</StatusChip>} delay={0.05} />
        <ListingRow img={riyadhImg} title={m.row2} price="85,000" per={m.perYear} chip={<StatusChip tone="review">{m.review}</StatusChip>} delay={0.15} />
      </div>
    </>
  );
}

function BrokerReply({ text, broker, delay }: { text: string; broker: string; delay: number }) {
  return (
    <Reveal delay={delay}>
      <div className="flex items-start gap-2 text-start">
        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-[#004D40] text-sm text-white dark:bg-[#1B7566]">
          <i className="ri-user-3-line" />
        </span>
        <div className="rounded-2xl rounded-ss-md bg-[var(--pv-color-surface-strong)] px-3 py-2 shadow-[0_8px_20px_-12px_rgba(16,24,40,0.3)] ring-1 ring-[var(--pv-border-subtle)]">
          <p className="flex items-center gap-1 text-[10.5px] font-semibold text-[var(--pv-color-text-muted)]">
            {broker}
            {/* the verified mark: gold, as THEME.md registers it */}
            <i className="ri-verified-badge-fill text-[12px] text-[#B58A0A]" />
          </p>
          <p className="mt-0.5 text-[12.5px] text-[var(--pv-color-text)]">{text}</p>
        </div>
      </div>
    </Reveal>
  );
}

/** أرسل طلبك — you ask once; licensed brokers answer. */
function RequestMock({ m }: { m: Mock }) {
  return (
    <div className="absolute inset-x-6 bottom-5 space-y-2.5">
      <Reveal delay={0.05}>
        {/* your own message sits at the inline END, as in any chat */}
        <div className="ms-auto w-fit max-w-[85%] rounded-2xl rounded-ee-md bg-[#004D40] px-3.5 py-2.5 text-start text-[12.5px] leading-snug text-white dark:bg-[#1B7566]">
          {m.ask}
        </div>
      </Reveal>
      <BrokerReply broker={m.broker} text={m.reply1} delay={0.2} />
      <BrokerReply broker={m.broker} text={m.reply2} delay={0.32} />
    </div>
  );
}

/** استثمر بالبيانات — a district's price range, from registered deeds. */
function InvestMock({ m }: { m: Mock }) {
  // Bar heights are shape only: no values, no axis. The market pages are a
  // statistical indicator, not a valuation, so the mockup claims no figure.
  const bars = [38, 52, 46, 64, 58, 72, 80];
  return (
    <PhoneTop>
      <Reveal delay={0.05}>
        <p className="text-[11.5px] font-semibold text-[var(--pv-color-text)]">{m.metric}</p>
        {/* low / median / high, as the market console draws a range. Numeric
            axes read left-to-right in both languages, as numerals are written. */}
        <div dir="ltr" className="relative mt-3 h-2 rounded-full bg-[var(--pv-color-surface-sunken)]">
          <span className="absolute inset-y-0 start-[18%] end-[22%] rounded-full bg-[#7AC3B7] dark:bg-[#1B7566]" />
          <span className="absolute -top-1 start-[47%] h-4 w-1 rounded-full bg-[#004D40] dark:bg-[#B7DDD7]" />
        </div>
        <div dir="ltr" className="mt-1.5 flex justify-between text-[10px] text-[var(--pv-color-text-muted)]">
          <span>{m.low}</span>
          <span>{m.mid}</span>
          <span>{m.high}</span>
        </div>
      </Reveal>
      <Reveal delay={0.18}>
        <div dir="ltr" className="mt-4 flex h-[64px] items-end gap-1.5">
          {bars.map((h, i) => (
            <span
              key={i}
              className={`flex-1 rounded-t-[3px] ${i === bars.length - 1 ? 'bg-[#004D40] dark:bg-[#3FBBA2]' : 'bg-[#B7DDD7] dark:bg-[#0A4A3E]'}`}
              style={{ height: `${h}%` }}
            />
          ))}
        </div>
        <p className="mt-2.5 text-[9.5px] text-[var(--pv-color-text-muted)]">{m.source}</p>
      </Reveal>
    </PhoneTop>
  );
}
